import type {
  RunsListRow,
  WorkflowTestExecutionDto,
} from '../../../features/workflows/useWorkflowTestExecutions';

export type TestExecutionChartDataPoint = {
  id: string;
  kind: 'cofactor' | 'external';
  index: number;
  date: string;
  passed: number;
  failed: number;
  running: number;
  flaky: number;
  status: RunsListRow['status'];
  isComplete: boolean;
};

function formatExecutionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isNativeExecutionComplete(status: WorkflowTestExecutionDto['status']): boolean {
  return (
    status === 'completed' ||
    status === 'completed_with_errors' ||
    status === 'completed_with_inconclusive'
  );
}

export function buildTestExecutionChartData(
  executions: RunsListRow[]
): TestExecutionChartDataPoint[] {
  return executions
    .filter((execution) => execution.startedAt)
    .sort(
      (left, right) => new Date(left.startedAt!).getTime() - new Date(right.startedAt!).getTime()
    )
    .flatMap((execution, index): TestExecutionChartDataPoint[] => {
      // Exhaustive switch on `kind` — never an `else`/ternary fallthrough. A
      // future third kind added to `RunsListRow` without a case here fails
      // typecheck via the `never` check in `default`, instead of silently
      // being read as native pass/fail counts it doesn't have.
      switch (execution.kind) {
        case 'external':
          // A flaky external test ultimately passed after a retry. Count it with
          // passing tests in the bar and pass-rate gauge, while preserving the
          // flaky count so the tooltip can disclose the retry.
          return [
            {
              id: execution.id,
              kind: 'external' as const,
              index,
              date: formatExecutionDate(execution.startedAt!),
              passed: execution.external.passed + execution.external.flaky,
              failed: execution.external.failed,
              // External list totals are folded from collected test results, not a
              // predeclared catalog size, so there is no honest unresolved count to
              // render while collection is in progress.
              running: 0,
              flaky: execution.external.flaky,
              status: execution.status,
              isComplete: execution.status === 'sealed',
            },
          ];
        case 'cofactor':
        case undefined: {
          const isRunning = execution.status === 'running' || execution.status === 'queued';
          return [
            {
              id: execution.id,
              kind: 'cofactor' as const,
              index,
              date: formatExecutionDate(execution.startedAt!),
              passed: execution.successfulWorkflows,
              failed: execution.failedWorkflows,
              running: isRunning ? execution.totalWorkflows - execution.completedWorkflows : 0,
              flaky: 0,
              status: execution.status,
              isComplete: isNativeExecutionComplete(execution.status),
            },
          ];
        }
        default: {
          const exhaustiveCheck: never = execution;
          return exhaustiveCheck;
        }
      }
    });
}

export function latestTestExecutionPassRate(data: TestExecutionChartDataPoint[]): number {
  const completed = data.filter((point) => point.isComplete);
  if (completed.length === 0) return 0;

  const latest = completed[completed.length - 1];
  const total = latest.passed + latest.failed;
  return total > 0 ? Math.round((latest.passed / total) * 100) : 0;
}
